import { Transcription } from '../types';
import { APIEndpoints } from '../config/APIEndpoints';

export interface WebSocketServiceCallbacks {
  onTranscription: (transcription: Transcription) => void;
  onAIResponse: (aiResponse: any) => void;
  onAIResponseStart: (data: any) => void;
  onAIResponseChunk: (data: any) => void;
  onAIResponseComplete: (data: any) => void;
  onAIResponseError: (data: any) => void;
  onConnectionChange: (connected: boolean) => void;
  onError: (error: Error) => void;
  onDisconnect?: () => void; // Optional callback to disconnect from LiveKit
}

interface ReconnectionConfig {
  maxAttempts: number;
  baseDelay: number;
  maxDelay: number;
  backoffMultiplier: number;
}

export class WebSocketService {
  private ws: WebSocket | null = null;
  private callbacks: WebSocketServiceCallbacks | null = null;
  private reconnectAttempts = 0;
  private reconnectTimeoutId: NodeJS.Timeout | null = null;
  private isConnecting = false;
  private shouldReconnect = true;
  private clientId: string; // Unique client ID that persists across reconnections
  private lastUrl: string | null = null;
  
  private readonly reconnectionConfig: ReconnectionConfig = {
    maxAttempts: 5,
    baseDelay: 1000,
    maxDelay: 30000,
    backoffMultiplier: 2
  };
  
  constructor() {
    this.connect = this.connect.bind(this);
    this.disconnect = this.disconnect.bind(this);
    this.isConnected = this.isConnected.bind(this);
    
    // Generate a persistent client ID for this session
    this.clientId = `client_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    console.log(`🆔 WebSocket client ID: ${this.clientId}`);
  }

  setCallbacks(callbacks: WebSocketServiceCallbacks) {
    this.callbacks = callbacks;
  }

  async connect(backendUrl?: string, room?: string): Promise<void> {
    if (this.isConnecting || this.isConnected()) {
      return;
    }

    // Clear any pending reconnection attempts
    this.clearReconnectTimeout();
    
    this.isConnecting = true;
    this.shouldReconnect = true;

    try {
      // Store URL for reconnection attempts
      const baseUrl = backendUrl ? `${backendUrl}/ws/transcriptions` : APIEndpoints.WEBSOCKET.TRANSCRIPTIONS;
      // **FIX FOR USER ISOLATION**: Include room parameter for filtering
      const wsUrl = room 
        ? `${baseUrl}?client_id=${this.clientId}&room=${encodeURIComponent(room)}`
        : `${baseUrl}?client_id=${this.clientId}`;
      this.lastUrl = wsUrl;
      
      console.log('Connecting to WebSocket:', wsUrl);
      if (room) {
        console.log('🏠 Joining room:', room);
      }
      
      this.ws = new WebSocket(wsUrl);
      this.setupWebSocketHandlers();

    } catch (error) {
      this.isConnecting = false;
      console.error('Failed to create WebSocket connection:', error);
      this.callbacks?.onError(error instanceof Error ? error : new Error('Failed to connect to WebSocket'));
      throw error;
    }
  }

  private setupWebSocketHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('✅ WebSocket connected');
      this.isConnecting = false;
      this.reconnectAttempts = 0;
      this.callbacks?.onConnectionChange(true);
    };

    this.ws.onmessage = (event) => {
      this.handleMessage(event);
    };

    this.ws.onclose = (event) => {
      this.handleClose(event);
    };

    this.ws.onerror = (error) => {
      this.handleError(error);
    };
  }

  private handleMessage(event: MessageEvent): void {
    try {
      const data = JSON.parse(event.data);
      console.log('📨 Received WebSocket message:', data);
      
      switch (data.type) {
        case 'transcription':
          this.handleTranscription(data);
          break;
        case 'ai_response':
          console.log('🤖 Received AI response:', data);
          this.callbacks?.onAIResponse(data);
          break;
        case 'ai_response_start':
          console.log('🚀 AI response started:', data);
          this.callbacks?.onAIResponseStart(data);
          break;
        case 'ai_response_chunk':
          console.log('📝 AI response chunk:', data);
          this.callbacks?.onAIResponseChunk(data);
          break;
        case 'ai_response_complete':
          console.log('✅ AI response completed:', data);
          this.callbacks?.onAIResponseComplete(data);
          break;
        case 'ai_response_error':
          console.log('❌ AI response error:', data);
          this.callbacks?.onAIResponseError(data);
          break;
        default:
          console.warn('Unknown message type:', data.type);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
      this.callbacks?.onError(new Error('Failed to parse WebSocket data'));
    }
  }

  private handleTranscription(data: any): void {
    const transcription: Transcription = {
      id: data.id,
      text: data.text,
      timestamp: new Date(data.timestamp),
      isQuestion: data.isQuestion || false,
      confidence: data.confidence || 0.95,
      source: data.source,
    };
    this.callbacks?.onTranscription(transcription);
  }

  private handleClose(event: CloseEvent): void {
    console.log('WebSocket disconnected:', event.code, event.reason);
    this.isConnecting = false;
    this.callbacks?.onConnectionChange(false);
    
    // Clean close (code 1000) means manual disconnect
    const isManualDisconnect = event.code === 1000;
    
    if (!isManualDisconnect) {
      console.log('WebSocket disconnected unexpectedly, also disconnecting from LiveKit');
      this.callbacks?.onDisconnect?.();
      
      // Attempt reconnection if enabled and not exceeded max attempts
      if (this.shouldReconnect && this.reconnectAttempts < this.reconnectionConfig.maxAttempts) {
        this.scheduleReconnect();
      } else if (this.reconnectAttempts >= this.reconnectionConfig.maxAttempts) {
        console.error('Max reconnection attempts reached. Please refresh the page.');
        this.callbacks?.onError(new Error('Failed to reconnect after multiple attempts'));
      }
    }
  }

  private handleError(error: Event): void {
    console.error('WebSocket error:', error);
    this.isConnecting = false;
    this.callbacks?.onError(new Error('WebSocket connection error'));
    
    // Disconnect from LiveKit on WebSocket error
    console.log('WebSocket error occurred, also disconnecting from LiveKit');
    this.callbacks?.onDisconnect?.();
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimeout();
    
    if (!this.shouldReconnect || this.reconnectAttempts >= this.reconnectionConfig.maxAttempts) {
      return;
    }
    
    this.reconnectAttempts++;
    
    // Calculate delay with exponential backoff
    const delay = Math.min(
      this.reconnectionConfig.baseDelay * Math.pow(this.reconnectionConfig.backoffMultiplier, this.reconnectAttempts - 1),
      this.reconnectionConfig.maxDelay
    );
    
    console.log(`⏳ Scheduling reconnection in ${delay}ms (attempt ${this.reconnectAttempts}/${this.reconnectionConfig.maxAttempts})`);
    
    this.reconnectTimeoutId = setTimeout(() => {
      if (!this.isConnected() && this.shouldReconnect) {
        console.log(`🔄 Attempting to reconnect (${this.reconnectAttempts}/${this.reconnectionConfig.maxAttempts})...`);
        this.connect().catch((error) => {
          console.error('Reconnection failed:', error);
        });
      }
    }, delay);
  }

  private clearReconnectTimeout(): void {
    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = null;
    }
  }

  disconnect(): void {
    this.shouldReconnect = false;
    this.clearReconnectTimeout();
    
    if (this.ws) {
      this.ws.close(1000, 'Manual disconnect');
      this.ws = null;
    }
    
    this.reconnectAttempts = 0;
    console.log('🔌 WebSocket manually disconnected');
  }

  isConnected(): boolean {
    return this.ws?.readyState === WebSocket.OPEN;
  }

  getConnectionState(): string {
    if (!this.ws) return 'disconnected';
    
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting';
      case WebSocket.OPEN:
        return 'connected';
      case WebSocket.CLOSING:
        return 'closing';
      case WebSocket.CLOSED:
        return 'disconnected';
      default:
        return 'unknown';
    }
  }
}
